"use client";

import { useCallback } from "react";
import { useTranslations } from "use-intl";

import { DealOfferPanel } from "@/components/marketplace/deal-offer-panel";
import { DealOfferPriceAlertProvider } from "@/components/marketplace/deal-offer-price-alert-context";
import { TrackedDealOffers } from "@/components/marketplace/deal-offers";
import type { DealOffersProps } from "@/components/marketplace/deal-offers-types";
import { useLists } from "@/contexts/lists-context";
import { useTrackingScope } from "@/hooks/use-tracking-scope";
import type { AddToListsTarget } from "@/lib/add-to-lists-target";

interface CatalogDealOffersProps extends DealOffersProps {
  target?: AddToListsTarget;
}

function CatalogDealOffersLoadingState() {
  const t = useTranslations("shared.affiliates");
  return (
    <DealOfferPanel className="animate-pulse" busy>
      <p className="sr-only" role="status">
        {t("catalogOffersLoading")}
      </p>
      <div aria-hidden="true" className="flex flex-col gap-3.5">
        <div className="flex items-center justify-between gap-3">
          <div className="h-3 w-40 rounded bg-muted" />
          <div className="h-3 w-24 rounded bg-muted" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col gap-2">
            <div className="h-9 w-40 rounded bg-muted" />
            <div className="h-5 w-32 rounded bg-muted" />
            <div className="h-3 w-56 rounded bg-muted" />
          </div>
          <div className="h-13 w-full rounded-lg bg-muted sm:w-56" />
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-muted" />
      </div>
    </DealOfferPanel>
  );
}

export function CatalogDealOffers({ target, ...offerProps }: CatalogDealOffersProps) {
  const t = useTranslations("shared.affiliates");
  const { openAddToLists } = useLists();
  const trackingScope = useTrackingScope();

  const openWishlist = useCallback(() => {
    if (!target) return;
    openAddToLists({ target, preferredListType: "wishlist", trackingScope });
  }, [openAddToLists, target, trackingScope]);

  const resolvedErrorState = (
    <DealOfferPanel compact className="border-destructive/30 bg-destructive/5">
      <p className="text-sm font-medium text-foreground" role="alert">
        {t("catalogOffersError")}
      </p>
    </DealOfferPanel>
  );

  return (
    <DealOfferPriceAlertProvider onPriceAlert={target ? openWishlist : null}>
      <TrackedDealOffers
        {...offerProps}
        resolvedErrorState={resolvedErrorState}
        resolvedLoadingState={<CatalogDealOffersLoadingState />}
      />
    </DealOfferPriceAlertProvider>
  );
}
